if (!customElements.get('mav-titan')) {
  customElements.define('mav-titan', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      const thumbs = this.querySelector('.titan__thumbs');
      const media = this.querySelector('.titan__hero-media');
      const stage = this.querySelector('[data-gallery-stage]');
      stage.append(this.querySelector('[data-illustration-note]'));
      media.append(thumbs);
      let gesture = null;
      stage.addEventListener('pointerdown', event => {
        if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) {
          gesture = null;
          return;
        }
        gesture = { id: event.pointerId, x: event.clientX, y: event.clientY };
        stage.setPointerCapture(event.pointerId);
      }, options);
      stage.addEventListener('pointerup', event => {
        if (!gesture || gesture.id !== event.pointerId) return;
        const dx = event.clientX - gesture.x;
        const dy = event.clientY - gesture.y;
        gesture = null;
        if (Math.abs(dx) < 45 || Math.abs(dx) <= Math.abs(dy) * 1.25) return;
        const buttons = [...thumbs.querySelectorAll('[data-gallery-image]')];
        if (buttons.length < 2) return;
        const current = buttons.findIndex(button => button.getAttribute('aria-pressed') === 'true');
        const next = buttons[(current + (dx < 0 ? 1 : -1) + buttons.length) % buttons.length];
        next.click();
        const row = thumbs.getBoundingClientRect();
        const tile = next.getBoundingClientRect();
        if (tile.left < row.left) thumbs.scrollLeft -= row.left - tile.left + 12;
        else if (tile.right > row.right) thumbs.scrollLeft += tile.right - row.right + 12;
      }, options);
      stage.addEventListener('pointercancel', () => { gesture = null; }, options);
      stage.addEventListener('lostpointercapture', () => { gesture = null; }, options);
      stage.addEventListener('dragstart', event => event.preventDefault(), options);
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = this.motion.matches;
      this.videos = [...this.querySelectorAll('[data-preview-video]')];
      this.visibleVideos = new Set();
      this.toggle = this.querySelector('[data-preview-toggle]');
      this.updateToggle();
      this.videoObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => entry.isIntersecting ? this.visibleVideos.add(entry.target) : this.visibleVideos.delete(entry.target));
        this.refreshVideos();
      }, { threshold: 0.2 });
      this.videos.forEach(video => this.videoObserver.observe(video));
      this.motion.addEventListener('change', event => {
        this.paused = event.matches;
        this.updateToggle();
        this.refreshVideos();
      }, options);
      document.addEventListener('visibilitychange', () => this.refreshVideos(), options);
      this.addEventListener('click', event => {
        const thumb = event.target.closest('[data-gallery-image]');
        if (thumb) {
          const image = this.querySelector('[data-gallery-stage] img');
          image.removeAttribute('srcset');
          image.removeAttribute('sizes');
          image.src = thumb.dataset.galleryImage;
          image.alt = thumb.dataset.imageAlt;
          image.style.objectFit = thumb.dataset.imageFit;
          this.querySelector('[data-illustration-note]').hidden = thumb.dataset.illustration !== 'true';
          this.querySelectorAll('[data-gallery-image]').forEach(button => button.setAttribute('aria-pressed', String(button === thumb)));
        }
        const quantity = event.target.closest('[data-quantity-step]');
        if (quantity) {
          const input = this.querySelector('[name="quantity"]');
          if (Number(quantity.dataset.quantityStep) > 0) input.stepUp(); else input.stepDown();
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
        if (event.target.closest('[data-preview-toggle]')) {
          this.paused = !this.paused;
          this.updateToggle();
          this.refreshVideos();
        }
        const play = event.target.closest('[data-play-video]');
        if (play) {
          this.closeVideo();
          const video = play.parentElement.querySelector('video');
          this.activeVideo = video;
          video.currentTime = 0;
          video.muted = false;
          video.controls = true;
          video.loop = false;
          play.hidden = true;
          play.parentElement.classList.add('is-playing');
          play.closest('article').querySelector('[data-close-video]').hidden = false;
          this.refreshVideos();
          video.play().catch(() => {});
          video.focus();
        }
        if (event.target.closest('[data-close-video]')) {
          const button = this.activeVideo?.parentElement.querySelector('[data-play-video]');
          this.closeVideo();
          this.refreshVideos();
          button?.focus();
        }
      }, options);
      const quantityInput = this.querySelector('[name="quantity"]');
      const quantityMirror = this.querySelector('[data-quantity-mirror]');
      quantityInput.addEventListener('change', () => { quantityMirror.value = quantityInput.value; }, options);
      quantityMirror.addEventListener('change', () => {
        const minimum = Number(quantityInput.min) || 1;
        const maximum = Number(quantityInput.max) || Infinity;
        quantityInput.value = Math.min(maximum, Math.max(minimum, Number(quantityMirror.value) || minimum));
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }, options);
      this.querySelector('[data-variant-select]')?.addEventListener('change', event => {
        const option = event.target.selectedOptions[0];
        this.querySelectorAll('[data-product-price]').forEach(el => el.textContent = option.dataset.price);
        const compare = this.querySelector('[data-compare-price]');
        compare.textContent = option.dataset.compare;
        compare.hidden = option.dataset.sale !== 'true';
        const button = this.querySelector('[type="submit"]');
        button.disabled = option.dataset.available !== 'true';
        button.querySelector('[data-add-text]').textContent = button.disabled ? button.dataset.soldLabel : button.dataset.addLabel;
        this.querySelectorAll('[data-secondary-add]').forEach(secondary => {
          secondary.disabled = button.disabled;
          secondary.firstElementChild.textContent = button.querySelector('[data-add-text]').textContent;
        });
        const url = new URL(location.href);
        url.searchParams.set('variant', option.value);
        history.replaceState({}, '', url);
      }, options);
      const sticky = this.querySelector('[data-sticky-buy]');
      const hero = this.querySelector('.titan__hero');
      const bottom = this.querySelector('.titan__bottom-cta');
      const updateSticky = () => { sticky.hidden = !(hero.getBoundingClientRect().bottom < 0 && bottom.getBoundingClientRect().top > innerHeight); };
      this.stickyObserver = new IntersectionObserver(updateSticky);
      [hero, bottom].forEach(el => this.stickyObserver.observe(el));
      updateSticky();
    }
    updateToggle() {
      if (!this.toggle) return;
      this.toggle.textContent = this.paused ? this.toggle.dataset.playLabel : this.toggle.dataset.pauseLabel;
      this.toggle.setAttribute('aria-pressed', String(this.paused));
    }
    refreshVideos() {
      this.videos.forEach(video => {
        if (video === this.activeVideo) { if (document.hidden) video.pause(); return; }
        if (!this.activeVideo && !this.paused && !document.hidden && this.visibleVideos.has(video)) {
          video.muted = true;
          video.play().catch(() => {});
        } else video.pause();
      });
    }
    closeVideo() {
      if (!this.activeVideo) return;
      const video = this.activeVideo;
      video.pause();
      video.muted = true;
      video.controls = false;
      video.loop = true;
      video.parentElement.classList.remove('is-playing');
      video.parentElement.querySelector('[data-play-video]').hidden = false;
      video.closest('article').querySelector('[data-close-video]').hidden = true;
      this.activeVideo = null;
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.videoObserver?.disconnect();
      this.stickyObserver?.disconnect();
      this.closeVideo();
      this.videos?.forEach(video => video.pause());
      this.initialized = false;
    }
  });
}

